import { useEffect, useRef, useState } from 'react'
import { Alert, Flex, Spin, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

const SCAN_INTERVAL_MS = 200

type CameraState = 'starting' | 'scanning' | 'denied' | 'unavailable'

export function QrScanner({ onDecoded }: { onDecoded: (text: string) => void }) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const onDecodedRef = useRef(onDecoded)
  const [camera, setCamera] = useState<CameraState>('starting')

  useEffect(() => {
    onDecodedRef.current = onDecoded
  }, [onDecoded])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamera('unavailable')
      return
    }

    const canvas = document.createElement('canvas')
    const decoder = import('./qrDecode')
    let stream: MediaStream | null = null
    let frame = 0
    let busy = false
    let lastScan = 0
    let stopped = false

    const stop = () => {
      stopped = true
      cancelAnimationFrame(frame)
      stream?.getTracks().forEach((track) => track.stop())
      video.srcObject = null
    }

    const tick = (now: number) => {
      if (stopped) return
      frame = requestAnimationFrame(tick)
      if (busy || now - lastScan < SCAN_INTERVAL_MS) return
      busy = true
      lastScan = now
      decoder
        .then(({ decodeVideoFrame }) => decodeVideoFrame(video, canvas))
        .then((text) => {
          if (text && !stopped) {
            stop()
            onDecodedRef.current(text)
          }
        })
        .catch(() => undefined)
        .finally(() => {
          busy = false
        })
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then(async (media) => {
        if (stopped) {
          media.getTracks().forEach((track) => track.stop())
          return
        }
        stream = media
        video.srcObject = media
        await video.play()
        setCamera('scanning')
        frame = requestAnimationFrame(tick)
      })
      .catch((error: unknown) => {
        if (stopped) return
        const denied = error instanceof DOMException && error.name === 'NotAllowedError'
        setCamera(denied ? 'denied' : 'unavailable')
      })

    return stop
  }, [])

  const failed = camera === 'denied' || camera === 'unavailable'

  return (
    <Flex vertical gap={12}>
      {failed ? (
        <Alert
          type="warning"
          showIcon
          message={t(camera === 'denied' ? 'voucher.cameraDenied' : 'voucher.cameraUnavailable')}
        />
      ) : (
        <Typography.Text type="secondary">{t('voucher.scanHint')}</Typography.Text>
      )}
      <div
        style={{
          position: 'relative',
          display: failed ? 'none' : 'block',
          aspectRatio: '1 / 1',
          maxHeight: 360,
          borderRadius: 12,
          overflow: 'hidden',
          background: '#000',
        }}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {camera === 'starting' ? (
          <Flex align="center" justify="center" style={{ position: 'absolute', inset: 0 }}>
            <Spin tip={t('voucher.cameraStarting')}>
              <div style={{ width: 120, height: 60 }} />
            </Spin>
          </Flex>
        ) : null}
      </div>
    </Flex>
  )
}
